import { useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Euro, FileText, Calendar } from 'lucide-react';
import { Card } from '../components/ui/card';
import { PropertyCard } from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { formatEuro } from '../utils/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedEstado, setSelectedEstado] = useState('Todos');
  const [stats, setStats] = useState({
    total: 0,
    activos: 0,
    valorTotal: 0,
    beneficioTotal: 0,
    porEstado: {},
    mdec: 0
  });
  const [loading, setLoading] = useState(true);

  const estados = ['Todos', 'Activo', 'Ofertado', 'Arras', 'En cartera', 'Reservado', 'Vendido', 'Descartado'];

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    filterByEstado();
  }, [selectedEstado, properties]);

  const filterByEstado = () => {
    if (selectedEstado === 'Todos') {
      setFilteredProperties(properties);
    } else {
      setFilteredProperties(properties.filter(p => p.estado === selectedEstado));
    }
  };

  const loadProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      const props = response.data;
      setProperties(props);
      
      const activos = props.filter(p => p.estado === 'Activo').length;
      const valorTotal = props.reduce((sum, p) => sum + p.precio_compra, 0);
      const beneficioTotal = props.reduce((sum, p) => sum + p.beneficio, 0);
      
      // Calcular totales por estado
      const estadosTotales = {};
      estados.filter(e => e !== 'Todos').forEach(estado => {
        estadosTotales[estado] = props.filter(p => p.estado === estado).length;
      });
      
      // Calcular MDEC (Media de Días En Cartera)
      const inmueblesConDias = props.filter(p => p.dias_en_cartera && p.dias_en_cartera > 0);
      const sumaDias = inmueblesConDias.reduce((sum, p) => sum + p.dias_en_cartera, 0);
      const mdec = inmueblesConDias.length > 0 ? sumaDias / inmueblesConDias.length : 0;
      
      setStats({
        total: props.length,
        activos,
        valorTotal,
        beneficioTotal,
        porEstado: estadosTotales,
        mdec
      });
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar inmuebles:', error);
      toast.error('Error al cargar los inmuebles');
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard</h1>
          <p className="text-xs text-muted-foreground mt-1">Gestión de tu cartera inmobiliaria</p>
        </div>
        <Button 
          data-testid="create-property-btn"
          onClick={() => navigate('/properties/new')} 
          size="sm"
          className="shadow-md w-full sm:w-auto"
        >
          <Building2 className="mr-2" size={16} />
          Nuevo Inmueble
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Card className="px-3 md:px-4 py-2 shadow-sm hover:shadow-md transition-shadow bg-gray-100">
          <p className="text-[10px] md:text-xs text-muted-foreground">Total Inmuebles</p>
          <div className="flex items-center gap-2">
            <Building2 className="text-primary" size={16} />
            <p className="text-base md:text-lg font-bold" data-testid="stat-total">{stats.total}</p>
          </div>
        </Card>

        <Card className="px-3 md:px-4 py-2 shadow-sm hover:shadow-md transition-shadow bg-gray-100">
          <p className="text-[10px] md:text-xs text-muted-foreground">Media días en cartera</p>
          <div className="flex items-center gap-2">
            <Calendar className="text-primary" size={16} />
            <p className="text-base md:text-lg font-bold" data-testid="stat-mdec">{stats.mdec ? stats.mdec.toFixed(0) : '0'} días</p>
          </div>
        </Card>
      </div>

      {/* Totales por Estado */}
      <Card className="p-3 md:p-4 shadow-sm">
        <h2 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Totales por Estado</h2>
        <div className="grid grid-cols-4 md:grid-cols-7 gap-1.5 md:gap-2">
          {estados.filter(e => e !== 'Todos').map(estado => {
            const bgColor = {
              'Activo': 'bg-sky-100 hover:bg-sky-200',
              'Ofertado': 'bg-amber-100 hover:bg-amber-200',
              'Arras': 'bg-orange-100 hover:bg-orange-200',
              'En cartera': 'bg-green-100 hover:bg-green-200',
              'Reservado': 'bg-red-100 hover:bg-red-200',
              'Vendido': 'bg-gray-200 hover:bg-gray-300',
              'Descartado': 'bg-gray-100 hover:bg-gray-200'
            }[estado] || 'bg-muted/30 hover:bg-muted/50';
            
            return (
              <div 
                key={estado} 
                className={`text-center px-1.5 md:px-2 py-1 rounded-lg cursor-pointer transition-colors ${bgColor}`}
                onClick={() => setSelectedEstado(estado)}
                data-testid={`estado-total-${estado.toLowerCase()}`}
              >
                <p className="text-sm md:text-base font-bold text-primary">{stats.porEstado?.[estado] || 0}</p>
                <p className="text-[8px] md:text-[10px] text-muted-foreground leading-tight">{estado}</p>
              </div>
            );
          })}
        </div>
      </Card>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm md:text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Inmuebles</h2>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/properties')}
            data-testid="view-all-btn"
            className="text-xs"
          >
            Ver Todos
          </Button>
        </div>

        {/* Filtros con scroll horizontal en móvil */}
        <div className="flex gap-1 mb-4 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap scrollbar-hide">
          {estados.map(estado => (
            <Button
              key={estado}
              variant={selectedEstado === estado ? 'default' : 'outline'}
              size="sm"
              className="text-xs px-2 py-1 h-7 flex-shrink-0"
              onClick={() => setSelectedEstado(estado)}
              data-testid={`filter-${estado.toLowerCase()}`}
            >
              {estado}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Cargando...</div>
        ) : filteredProperties.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {filteredProperties.map(property => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        ) : (
          <Card className="p-6 md:p-8 text-center">
            <Building2 className="mx-auto text-muted-foreground mb-3" size={32} />
            <p className="text-muted-foreground text-sm mb-3">
              No hay inmuebles {selectedEstado !== 'Todos' ? `en estado "${selectedEstado}"` : 'registrados'}
            </p>
            <Button size="sm" onClick={() => navigate('/properties/new')} data-testid="empty-create-btn">
              Crear Primer Inmueble
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}